import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { FilterChip } from '@/shared/components/cse'
import type { PropertySort } from '../../types/property'
import type { SearchKey, UpdateSearch } from './types'

export type ActiveFilterInputs = {
  q: string
  city: string
  country: string
  type: string
  availableFrom: string
  availableTo: string
  guests: string
  minBedrooms: string
  minBathrooms: string
  minPrice: string
  maxPrice: string
  amenities: string[]
  accessibility: string[]
  cancellation: string
  hasAvailability: boolean
  onlyActive: boolean
  sort: PropertySort
}

export function useActiveFilterChips(inputs: ActiveFilterInputs, updateSearch: UpdateSearch): FilterChip[] {
  const { t } = useTranslation()
  const {
    q,
    city,
    country,
    type,
    availableFrom,
    availableTo,
    guests,
    minBedrooms,
    minBathrooms,
    minPrice,
    maxPrice,
    amenities,
    accessibility,
    cancellation,
    hasAvailability,
    onlyActive,
    sort,
  } = inputs

  return useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = []
    const pushText = (key: SearchKey, labelKey: string, value: string, clearKey: string) => {
      if (value.trim()) {
        chips.push({
          key,
          label: t(labelKey),
          value: value.trim(),
          removeLabel: t(clearKey),
          onRemove: () => updateSearch({ [key]: null }),
        })
      }
    }

    pushText('q', 'reservation.properties.filters.search_chip', q, 'reservation.properties.filters.clear_search')
    pushText('city', 'reservation.properties.filters.city_chip', city, 'reservation.properties.filters.clear_city')
    pushText('country', 'reservation.properties.filters.country', country, 'reservation.properties.filters.clear_country')
    pushText('type', 'reservation.properties.filters.type', type, 'reservation.properties.filters.clear_type')
    pushText('available_from', 'reservation.properties.filters.available_from_chip', availableFrom, 'reservation.properties.filters.clear_available_from')
    pushText('available_to', 'reservation.properties.filters.available_to_chip', availableTo, 'reservation.properties.filters.clear_available_to')
    pushText('guests', 'reservation.properties.filters.guests_chip', guests, 'reservation.properties.filters.clear_guests')
    pushText('min_bedrooms', 'reservation.properties.filters.bedrooms_chip', minBedrooms, 'reservation.properties.filters.clear_bedrooms')
    pushText('min_bathrooms', 'reservation.properties.filters.bathrooms_chip', minBathrooms, 'reservation.properties.filters.clear_bathrooms')
    pushText('min_price', 'reservation.properties.filters.min_price_chip', minPrice, 'reservation.properties.filters.clear_min_price')
    pushText('max_price', 'reservation.properties.filters.max_price_chip', maxPrice, 'reservation.properties.filters.clear_max_price')

    amenities.forEach((value) =>
      chips.push({
        key: `amenity-${value}`,
        label: t('reservation.properties.filters.amenities_chip'),
        value: t(`reservation.properties.amenities.${value}`, { defaultValue: value }),
        removeLabel: t('reservation.properties.filters.clear_amenity'),
        onRemove: () => updateSearch({ amenities: amenities.filter((v) => v !== value).join(',') || null }),
      }),
    )
    accessibility.forEach((value) =>
      chips.push({
        key: `accessibility-${value}`,
        label: t('reservation.properties.filters.accessibility_chip'),
        value: t(`reservation.properties.amenities.${value}`, { defaultValue: value }),
        removeLabel: t('reservation.properties.filters.clear_accessibility'),
        onRemove: () => updateSearch({ accessibility: accessibility.filter((v) => v !== value).join(',') || null }),
      }),
    )

    if (cancellation) {
      chips.push({
        key: 'cancellation',
        label: t('reservation.properties.filters.cancellation_chip'),
        value: t(`reservation.properties.filters.cancellation_${cancellation}`),
        removeLabel: t('reservation.properties.filters.clear_cancellation'),
        onRemove: () => updateSearch({ cancellation: null }),
      })
    }
    if (hasAvailability) {
      chips.push({
        key: 'has_availability',
        label: t('reservation.properties.filters.has_availability_chip'),
        value: t('reservation.properties.filters.enabled'),
        removeLabel: t('reservation.properties.filters.clear_has_availability'),
        onRemove: () => updateSearch({ has_availability: null }),
      })
    }
    if (!onlyActive) {
      chips.push({
        key: 'only_active',
        label: t('reservation.properties.filters.only_active_chip'),
        value: t('reservation.properties.filters.include_closed'),
        removeLabel: t('reservation.properties.filters.clear_only_active'),
        onRemove: () => updateSearch({ only_active: null }),
      })
    }
    if (sort !== 'recommended') {
      chips.push({
        key: 'sort',
        label: t('reservation.properties.filters.sort_chip'),
        value: t(`reservation.properties.filters.sort_${sort}`),
        removeLabel: t('reservation.properties.filters.clear_sort'),
        onRemove: () => updateSearch({ sort: null }),
      })
    }

    return chips
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateSearch dérivée des params courants.
  }, [accessibility, amenities, availableFrom, availableTo, cancellation, city, country, guests, hasAvailability, maxPrice, minBathrooms, minBedrooms, minPrice, onlyActive, q, sort, t, type])
}
